import {
  useCanAccess,
  useGetIdentity,
  useGetList,
  type Identifier,
} from "ra-core";
import { useCallback, useMemo, useSyncExternalStore } from "react";

import type {
  Pipeline,
  PipelineKind,
  PipelineStage,
  Project,
  SalesProject,
} from "../types";
import { byPosition } from "./byPosition";

/** The owner's choice «Все проекты» in the switcher. */
export const ALL_PROJECTS = "all";
export type ProjectChoice = Identifier | typeof ALL_PROJECTS;

const LIST = { pagination: { page: 1, perPage: 1000 } };

// The chosen project (and deal pipeline of each project) is remembered per
// user in localStorage, outside ra-core's store: logging out resets that
// store, and the choice should survive it. A tiny external store keeps every
// component that reads the choice (header switcher, board, forms) in step.
const listeners = new Set<() => void>();
const readKey = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};
const writeKey = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // private mode or blocked storage: the choice lives until reload
  }
  listeners.forEach((listener) => listener());
};
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  window.addEventListener("storage", listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", listener);
  };
};
const useStoredValue = (key: string | null) =>
  useSyncExternalStore(subscribe, () => (key ? readKey(key) : null));

export const projectStorageKey = (salesId: Identifier) =>
  `vv.project.${salesId}`;
const pipelineStorageKey = (salesId: Identifier, projectId: Identifier) =>
  `vv.pipeline.${salesId}.${projectId}`;

/**
 * Projects, pipelines and stages the current user may see. The database
 * already limits them (RLS); the owner reads every project, the others only
 * those they have a sales_projects row for, which is also checked here so
 * that the list is right with any data provider.
 */
export const useProjects = () => {
  const { identity, isPending: identityPending } = useGetIdentity();
  const { canAccess: isOwner, isPending: accessPending } = useCanAccess({
    resource: "projects",
    action: "edit",
  });
  const { data: allProjects, isPending: projectsPending } = useGetList<Project>(
    "projects",
    {
      ...LIST,
      sort: { field: "position", order: "ASC" },
    },
  );
  const { data: memberships, isPending: membershipsPending } =
    useGetList<SalesProject>(
      "sales_projects",
      { ...LIST, filter: { sales_id: identity?.id } },
      { enabled: identity != null && accessPending === false && !isOwner },
    );
  const { data: pipelines, isPending: pipelinesPending } = useGetList<Pipeline>(
    "pipelines",
    {
      ...LIST,
      sort: { field: "position", order: "ASC" },
    },
  );
  const { data: stages, isPending: stagesPending } = useGetList<PipelineStage>(
    "pipeline_stages",
    {
      ...LIST,
      sort: { field: "position", order: "ASC" },
    },
  );

  const isPending =
    identityPending ||
    accessPending ||
    projectsPending ||
    (!isOwner && membershipsPending) ||
    pipelinesPending ||
    stagesPending;

  const projects = useMemo(() => {
    const mine = new Set((memberships ?? []).map((m) => String(m.project_id)));
    return (allProjects ?? [])
      .filter((p) => !p.archived && (isOwner || mine.has(String(p.id))))
      .sort(byPosition);
  }, [allProjects, memberships, isOwner]);

  const sortedPipelines = useMemo(
    () => [...(pipelines ?? [])].sort(byPosition),
    [pipelines],
  );
  const sortedStages = useMemo(
    () => [...(stages ?? [])].sort(byPosition),
    [stages],
  );

  const pipelinesOf = useCallback(
    (projectId: Identifier, kind: PipelineKind = "deal") =>
      sortedPipelines.filter(
        (p) => String(p.project_id) === String(projectId) && p.kind === kind,
      ),
    [sortedPipelines],
  );
  const stagesOf = useCallback(
    (pipelineId: Identifier | undefined) =>
      sortedStages.filter((s) => String(s.pipeline_id) === String(pipelineId)),
    [sortedStages],
  );

  return {
    identity,
    isOwner: isOwner === true,
    isPending,
    /** active projects the user works in (owner: all active ones) */
    projects,
    pipelines: sortedPipelines,
    stages: sortedStages,
    pipelinesOf,
    stagesOf,
  };
};

/**
 * The project chosen in the header switcher, remembered per user. Falls back
 * to the first project when nothing (or something no longer available) is
 * stored. «Все проекты» is offered to the owner only.
 */
export const useCurrentProject = () => {
  const data = useProjects();
  const { identity, isOwner, projects } = data;
  const key = identity?.id != null ? projectStorageKey(identity.id) : null;
  const stored = useStoredValue(key);

  let currentId: ProjectChoice | null = null;
  if (stored === ALL_PROJECTS && isOwner) {
    currentId = ALL_PROJECTS;
  } else {
    const found = projects.find((p) => String(p.id) === stored);
    currentId = found?.id ?? projects[0]?.id ?? null;
  }
  const current =
    currentId === ALL_PROJECTS
      ? null
      : (projects.find((p) => p.id === currentId) ?? null);

  const setCurrentProject = useCallback(
    (choice: ProjectChoice) => {
      if (key) writeKey(key, String(choice));
    },
    [key],
  );

  return { ...data, currentId, current, setCurrentProject };
};

/**
 * The deal pipeline shown for a project: the one picked in the tabs above the
 * board (remembered per user and project), else the project's first one.
 */
export const useCurrentDealPipeline = (projectId: Identifier | null) => {
  const { identity, pipelinesOf, stagesOf } = useProjects();
  const key =
    identity?.id != null && projectId != null
      ? pipelineStorageKey(identity.id, projectId)
      : null;
  const stored = useStoredValue(key);
  const pipelines = projectId != null ? pipelinesOf(projectId, "deal") : [];
  const pipeline =
    pipelines.find((p) => String(p.id) === stored) ?? pipelines[0] ?? null;
  const setPipeline = useCallback(
    (pipelineId: Identifier) => {
      if (key) writeKey(key, String(pipelineId));
    },
    [key],
  );
  return {
    pipelines,
    pipeline,
    stages: stagesOf(pipeline?.id),
    setPipeline,
  };
};
